import { attributes as mockAttributes, contentTypes as mockContentTypes, floors as mockFloors, genres as mockGenres } from "@/mocks/genres";
import { contentTypeConditionSets, services as mockServices } from "@/mocks/categoryConfig";
import { filterGenres, findGenreIn } from "@/lib/genreMatch";
import type {
  CategoryCatalogResponse,
  ContentTypeRecord,
  SearchableAttribute,
} from "@/types/category";
import type {
  AttributeDefinition,
  ContentTypeId,
  Floor,
  Genre,
} from "@/types/taxonomy";
import type { AudienceType } from "@/types/work";

/**
 * カテゴリ情報のスナップショット。
 *
 * 画面表示（ジャンル名・形式名・条件の見出し）は同期的に呼ばれるため、
 * リポジトリから取得した最新のカテゴリ情報をここに保持し、表示はこのキャッシュを参照する。
 *
 * モード（男性向け／女性向け）ごとにスナップショットを持つので、
 * 切り替え後に反対モードのジャンル情報が混ざることはない。
 * モード別の情報が無い場合は「全モード共通」のスナップショットを使う（ジャンル側の audienceIds で絞る）。
 *
 * フォールバック方針：
 *  - "demo"   … 取得前はデモデータで補完する（Mockリポジトリ利用時）
 *  - "strict" … 取得前は空を返す（本番HTTPモード。デモデータを本番情報として見せない）
 */

export type CategoryFallbackPolicy = "demo" | "strict";

let fallbackPolicy: CategoryFallbackPolicy = "demo";
let snapshots = new Map<string, CategoryCatalogResponse>();

const ALL_KEY = "all";

function buildDemoFallback(): CategoryCatalogResponse {
  return {
    contentTypes: mockContentTypes.map((type) => ({
      ...type,
      labelSource: "local",
      conditionSet: contentTypeConditionSets[type.id],
    })),
    services: mockServices,
    floors: mockFloors,
    genres: mockGenres,
    attributes: mockAttributes,
    source: "mock",
  };
}

function buildEmpty(source: CategoryCatalogResponse["source"]): CategoryCatalogResponse {
  return {
    contentTypes: [],
    services: [],
    floors: [],
    genres: [],
    attributes: [],
    source,
  };
}

/**
 * フォールバック方針を切り替える。
 * 本番HTTPリポジトリに切り替えたときは strict にして、デモカテゴリの無言の表示を避ける。
 */
export function setCategoryFallbackPolicy(policy: CategoryFallbackPolicy): void {
  if (fallbackPolicy === policy) return;
  fallbackPolicy = policy;
  snapshots = new Map();
}

export function getCategoryFallbackPolicy(): CategoryFallbackPolicy {
  return fallbackPolicy;
}

/** 取得済みのカテゴリ情報（モード別 → 共通 → 方針に応じたフォールバック） */
export function getCategorySnapshot(mode?: AudienceType): CategoryCatalogResponse {
  if (mode) {
    const scoped = snapshots.get(mode);
    if (scoped) return scoped;
  }
  const shared = snapshots.get(ALL_KEY);
  if (shared) {
    if (!mode) return shared;
    // 共通スナップショットしか無い場合は、モードで絞ったビューを返す（キャッシュはしない）
    return { ...shared, genres: filterGenres(shared.genres, { mode }) };
  }
  if (fallbackPolicy === "strict") return buildEmpty("http");
  return buildDemoFallback();
}

/** リポジトリから取得したカテゴリ情報を反映する（mode を指定するとモード別に保持） */
export function setCategorySnapshot(response: CategoryCatalogResponse, mode?: AudienceType): void {
  if (!response) return;
  const normalized: CategoryCatalogResponse = {
    ...response,
    contentTypes: response.contentTypes.map((type) => ({
      ...type,
      conditionSet: type.conditionSet ?? contentTypeConditionSets[type.id],
    })),
  };
  snapshots.set(mode ?? ALL_KEY, normalized);
}

export function resetCategorySnapshot(): void {
  snapshots = new Map();
}

export function getContentTypeRecords(): ContentTypeRecord[] {
  return getCategorySnapshot().contentTypes;
}

export function getContentTypeRecord(id: ContentTypeId): ContentTypeRecord | undefined {
  return getCategorySnapshot().contentTypes.find((type) => type.id === id);
}

export function getServiceRecords() {
  return getCategorySnapshot().services;
}

export function getAllFloors(): Floor[] {
  return getCategorySnapshot().floors;
}

export function getAllAttributes(): AttributeDefinition[] {
  return getCategorySnapshot().attributes;
}

export function getFloorById(id: string): Floor | undefined {
  return getCategorySnapshot().floors.find((floor) => floor.id === id);
}

export function getAllGenres(mode?: AudienceType): Genre[] {
  return getCategorySnapshot(mode).genres;
}

/** 提供元ジャンルID・サイト内ID・名称・別名・読みからジャンルを解決する */
export function findGenre(value: string, mode?: AudienceType): Genre | undefined {
  if (!value) return undefined;
  return findGenreIn(getAllGenres(mode), value) ?? findGenreIn(getAllGenres(), value);
}

export function getGenreDisplayName(value: string, mode?: AudienceType): string {
  return findGenre(value, mode)?.name ?? value;
}

export function isGenreVisible(genre: Genre, mode?: AudienceType): boolean {
  if (!mode) return true;
  return genre.audienceIds.includes(mode) || genre.audienceIds.includes("common");
}

export function getGenresFor(mode?: AudienceType): Genre[] {
  return filterGenres(getAllGenres(mode), { mode });
}

export function getGenreGroupsFor(mode?: AudienceType): { floor: Floor; genres: Genre[] }[] {
  const genres = getGenresFor(mode);
  return getAllFloors()
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((floor) => ({
      floor,
      genres: genres
        .filter((genre) => genre.floorId === floor.id)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    }))
    .filter((group) => group.genres.length > 0);
}

export function findGenresByQuery(query: string, mode?: AudienceType): Genre[] {
  return filterGenres(getAllGenres(mode), { mode, query });
}

export function findAttributeDefinition(id: string): AttributeDefinition | undefined {
  return getCategorySnapshot().attributes.find((attribute) => attribute.id === id);
}

export function getConditionSet(id: ContentTypeId) {
  return getContentTypeRecord(id)?.conditionSet ?? contentTypeConditionSets[id];
}

/** 指定した商品形式で使える属性（null はすべて） */
export function getSearchableAttributesFor(contentType: ContentTypeId | null): SearchableAttribute[] {
  const attributes = getCategorySnapshot().attributes;
  const conditionSet = contentType ? getConditionSet(contentType) : null;
  const allowedIds = conditionSet?.attributeIds;
  return attributes
    .filter((definition) => {
      if (allowedIds && allowedIds.length > 0) return allowedIds.includes(definition.id);
      if (!contentType) return true;
      return definition.contentTypeIds.length === 0 || definition.contentTypeIds.includes(contentType);
    })
    .map((definition) => ({
      definition,
      contentTypeIds:
        definition.contentTypeIds.length > 0 ? definition.contentTypeIds : [contentType ?? ("other" as ContentTypeId)],
      supportsRange: definition.valueType === "number",
      labelSource: conditionSet?.providerAttributeIds.includes(definition.id) ? "provider" : "local",
    }))
    .sort((a, b) => {
      const order = allowedIds ?? [];
      return order.indexOf(a.definition.id) - order.indexOf(b.definition.id);
    });
}